import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, LeadDeskData } from './crmApi';
import { HolidayBanner } from './Holidays';
import LeadPanel from './LeadPanel';
import { Empty, ShiftChip, Spinner, fmtHm, fmtPhone, fmtRel, goldBg, useCrmMe } from './ui';
import { C, Card, Columns, dayLabel, greeting, named, nf, weekdayLabel } from './viz';

// ── Lead Desk: the Lead Manager's home ───────────────────────
// What needs the Lead Manager first (requests to approve, late follow-ups
// and polls) and Junk Leads (nobody picked up after everyone's tries), then
// today's lead flow, how far each salesperson got with today's leads and
// their shift, who calls in what order, and this week's leads.
// Mirrors the app's CrmLeadDeskScreen.

const Flow: React.FC<{ n: number; label: string; hint: string; strong?: boolean }> = ({ n, label, hint, strong }) => (
  <div className={`rounded-2xl px-4 py-3 ${strong ? 'bg-white/15 border border-white/20' : 'bg-white/5 border border-white/10'}`}>
    <p className="text-3xl font-bold leading-none">{nf(n)}</p>
    <p className="text-xs font-bold mt-1.5">{label}</p>
    <p className="text-[10px] text-white/60">{hint}</p>
  </div>
);

// self: the signed-in Lead Manager / Sales Admin (not the admin), who decides
// whether they take new leads too (a Lead Manager gets the ones the
// salespeople couldn't reach either way)
const LeadDesk: React.FC<{ auth: CrmAuth; name: string; onNavigate: (page: string) => void; self?: { takesLeads: boolean; onChange: (v: boolean) => void } }> = ({ auth, name, onNavigate, self }) => {
  const { me } = useCrmMe(auth);
  const [d, setD] = useState<LeadDeskData | null>(null);
  const [failed, setFailed] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);
  const load = useCallback(async () => {
    try { setD(await crm.leadDesk(auth)); setFailed(''); } catch (e: any) { setFailed(e.message); toast.error(e.message); }
  }, [auth]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  const switchCalls = async () => {
    if (!self) return;
    setSwitching(true);
    try {
      const r = await crm.setTakesLeads(auth, !self.takesLeads);
      self.onChange(r.takesLeads);
      toast.success(r.takesLeads ? 'You take new leads now: they come to you in turn' : 'No new leads come to you now. Leads the salespeople could not reach still do.');
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSwitching(false); }
  };

  if (!d) return failed ? <Empty icon="⚠️" title="Could not load the lead desk" sub={failed} /> : <Spinner label="Loading the lead desk…" />;
  const f = d.flow;
  const takers = d.team.filter(t => t.inTurn);
  const requests = (f.leadRequests || 0) + (f.shiftRequests || 0) + (f.callFixes || 0);
  const needsYou = f.lateFollowUps + requests;
  const junk = d.junk || { total: 0, today: 0, leads: [] };
  const topSource = Math.max(1, ...d.sources.map(s => s.n));

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-3xl p-5 sm:p-7 text-white" style={{ background: 'linear-gradient(135deg,#1a1428 0%,#2b2240 50%,#3d2f5f 100%)' }}>
        <div className="absolute -top-24 right-0 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(240,180,41,0.22), transparent 70%)' }} />
        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="text-white/60 text-sm">{greeting()}, {name.split(' ')[0]} 👋</p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">Lead desk</h1>
            <p className="text-white/70 text-sm mt-1">
              <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${d.working ? 'bg-green-400' : 'bg-gray-400'}`} />
              {d.working ? 'Working hours: new leads go out in turn now' : 'Outside working hours: new leads wait for the next working morning'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            {self && (
              <button onClick={switchCalls} disabled={switching} role="switch" aria-checked={self.takesLeads}
                className="px-4 py-2.5 rounded-xl text-sm font-bold border border-white/25 hover:bg-white/10 disabled:opacity-60 flex items-center gap-2">
                <span className={`w-9 h-5 rounded-full relative transition-colors ${self.takesLeads ? 'bg-green-400' : 'bg-white/25'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${self.takesLeads ? 'left-[18px]' : 'left-0.5'}`} />
                </span>
                {self.takesLeads ? 'I take new leads' : 'Not taking new leads'}
              </button>
            )}
            {requests > 0 && (
              <button onClick={() => onNavigate('requests')} className="px-4 py-2.5 rounded-xl text-sm font-bold bg-white text-purple-800 hover:bg-purple-50">
                📝 {requests} request{requests === 1 ? '' : 's'} to approve
              </button>
            )}
            <button onClick={() => onNavigate('add')} className="px-4 py-2.5 rounded-xl text-sm font-bold text-white" style={goldBg}>＋ Add leads</button>
            <button onClick={() => onNavigate('monitor')} className="px-4 py-2.5 rounded-xl text-sm font-bold border border-white/25 text-white/90 hover:bg-white/10">📈 Team numbers</button>
            <button onClick={() => onNavigate('leads')} className="px-4 py-2.5 rounded-xl text-sm font-bold border border-white/25 text-white/90 hover:bg-white/10">All leads</button>
          </div>
        </div>
        <div className="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mt-5">
          <Flow n={f.addedToday} label="Added today" hint={`${nf(f.addedThisWeek)} this week`} strong />
          <Flow n={f.handedOutToday} label="Handed out" hint="in turn, today" />
          <Flow n={f.movedToday} label="Moved on" hint="not taken yesterday" />
          <Flow n={f.notCalled} label="Not called yet" hint="new, untouched" />
          <Flow n={f.movingTomorrow} label="Move tomorrow" hint="not reached, or Call Later with no brochure" />
          <Flow n={f.waiting.total} label="Waiting" hint="no salesperson yet" strong={f.waiting.total > 0} />
        </div>
      </div>

      <HolidayBanner note={d.holiday} manager onOpen={() => onNavigate('team:holidays')} />

      {/* What needs the Lead Manager */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={needsYou ? `⚠️ ${nf(needsYou)} need${needsYou === 1 ? 's' : ''} you` : '✅ Nothing needs you'} sub="Requests to approve and follow-ups nobody did; and the leads nobody picked up after everyone's tries" className="lg:col-span-2"
          right={junk.total > 0 ? <button onClick={() => onNavigate('leads:junk')} className="text-xs font-bold text-purple-600 whitespace-nowrap">Open Junk Leads →</button> : undefined}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button onClick={() => onNavigate('requests')} className="text-left rounded-xl bg-purple-50 border border-purple-100 p-3 hover:border-purple-200">
              <p className="text-2xl font-bold text-gray-900">{nf(requests)}</p>
              <p className="text-xs font-bold text-gray-700">Requests to approve</p>
              <p className="text-[11px] text-gray-500">{[
                f.leadRequests ? `${nf(f.leadRequests)} lead${f.leadRequests === 1 ? '' : 's'}` : '',
                f.shiftRequests ? `${nf(f.shiftRequests)} shift change${f.shiftRequests === 1 ? '' : 's'}` : '',
                f.callFixes ? `${nf(f.callFixes)} call fix${f.callFixes === 1 ? '' : 'es'}` : '',
              ].filter(Boolean).join(' · ') || 'Lead, shift and call fix requests'} →</p>
            </button>
            <button onClick={() => onNavigate('leads:junk')} className="text-left rounded-xl bg-pink-50 border border-pink-100 p-3 hover:border-pink-200">
              <p className="text-2xl font-bold text-gray-900">{nf(junk.total)}</p>
              <p className="text-xs font-bold text-gray-700">Junk Leads</p>
              <p className="text-[11px] text-gray-500">{junk.today ? `${nf(junk.today)} today · ` : ''}not picked after everyone's tries, or didn't ring 2 times →</p>
            </button>
            <button onClick={() => onNavigate('team')} className="text-left rounded-xl bg-red-50 border border-red-100 p-3 hover:border-red-200">
              <p className="text-2xl font-bold text-gray-900">{nf(f.lateFollowUps)}</p>
              <p className="text-xs font-bold text-gray-700">Follow-ups gone late</p>
              <p className="text-[11px] text-gray-500">{f.pollsDue ? `${nf(f.pollsDue)} poll${f.pollsDue === 1 ? '' : 's'} due now · ` : ''}see whose on Team →</p>
            </button>
          </div>
          {junk.leads.length > 0 && (
            <>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mt-4">Newest in Junk Leads</p>
              <ul className="mt-1 divide-y divide-gray-100">
                {junk.leads.map(l => (
                  <li key={l.id}>
                    <button onClick={() => setOpen(l.id)} className="w-full text-left py-2 flex items-center gap-3 hover:bg-purple-50/40 rounded-lg px-1">
                      <span className="flex-1 min-w-0 text-sm font-semibold text-gray-800 truncate">{l.name || fmtPhone(l.phone)} <span className="text-[11px] font-normal text-gray-400">#{l.leadNo} · {l.reason || l.source || 'no source'}</span></span>
                      <span className="text-[11px] text-gray-500 whitespace-nowrap">{fmtRel(l.closedAt).replace(' late', '')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {(f.waiting.afterHours > 0 || f.waiting.nobodyFree > 0) && (
            <p className="text-[11px] text-gray-500 mt-3">Also waiting, these go out by themselves: {f.waiting.afterHours} for the next working morning, {f.waiting.nobodyFree} for someone free.</p>
          )}
        </Card>
        <Card title="Who calls, in order" sub="New leads go to whoever is on shift. A lead nobody picks up: each salesperson tries 3 times, then each Lead Manager 3 times, then Junk Leads. One whose phone didn't ring 2 times (switched off, not reachable…, and never rang) goes to Junk Leads straight away."
          right={<button onClick={() => onNavigate('rules')} className="text-xs font-bold text-purple-600 whitespace-nowrap">Change →</button>}>
          {d.order.length === 0 ? <p className="text-sm text-gray-400">Nobody is taking leads.</p> : (
            <ol className="space-y-1.5">
              {d.order.map((p, i) => (
                <li key={p.id} className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2">
                  <span className="w-6 h-6 flex-shrink-0 rounded-full text-[11px] font-bold flex items-center justify-center text-white" style={{ background: '#2b2240' }}>{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{p.name}</span>
                    {p.role === 'Lead Manager' && <span className="block text-[10px] text-gray-500">Lead Manager · {p.newLeads ? 'new leads too' : 'leads not picked only'}</span>}
                    {p.shift && <span className="block mt-1"><ShiftChip shift={p.shift} onNow={p.onShiftNow} compact /></span>}
                  </span>
                  {p.onLeave ? <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">On leave</span>
                    : p.awayNow && p.halfDay ? <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 whitespace-nowrap">Half day till {fmtHm(p.halfDay.to)}</span>
                    : p.paused ? <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">Paused</span>
                    : <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-800 whitespace-nowrap">Taking leads</span>}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {/* Each salesperson with today's leads */}
      <Card title="Team today" sub="How far each salesperson got with today's leads"
        right={<button onClick={() => onNavigate('team')} className="text-xs font-bold text-purple-600 whitespace-nowrap">Full Team Today →</button>}>
        {takers.length === 0 ? <p className="text-sm text-gray-400">Nobody is taking leads.</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {takers.map(t => {
              const got = t.givenToday + t.movedToday;
              const done = Math.max(0, got - t.notTaken);
              const pctDone = got ? Math.round((done / got) * 100) : 0;
              return (
                <div key={t.id} className="rounded-2xl border border-gray-100 p-3">
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-bold text-gray-800 truncate">{t.name}</p>
                    {t.halfDay && !t.onLeave ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 whitespace-nowrap">Half day {fmtHm(t.halfDay.from)} – {fmtHm(t.halfDay.to)}</span> : null}
                    {t.onLeave ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">On leave</span>
                      : t.paused ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">Paused</span> : null}
                  </div>
                  <div className="mt-1"><ShiftChip shift={t.shift} onNow={t.onShiftNow} /></div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-bold" style={{ color: C.ink }}>{t.calledToday}</span><span className="text-xs text-gray-500">calls today · {t.talkedToday} talked to</span>
                  </div>
                  <div className="h-2 rounded-full mt-2" style={{ background: '#e7eef9' }} role="meter" aria-valuemin={0} aria-valuemax={got} aria-valuenow={done} aria-label={`${t.name}: ${done} of ${got} of today's leads taken`}>
                    <div className="h-2 rounded-full" style={{ width: `${got ? Math.max(done ? 4 : 0, pctDone) : 0}%`, background: C.leads }} />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">{got ? `${done} of ${got} of today's leads taken` : 'No leads given today'}</p>
                  <div className="flex gap-3 mt-2 text-[11px]">
                    <span className={t.toCall ? 'font-bold text-orange-700' : 'text-gray-400'}>{t.toCall} to call</span>
                    <span className={t.lateFollowUps ? 'font-bold text-red-600' : 'text-gray-400'}>{t.lateFollowUps} late</span>
                    <span className={t.pollsDue ? 'font-bold text-fuchsia-700' : 'text-gray-400'}>{t.pollsDue} poll{t.pollsDue === 1 ? '' : 's'} due</span>
                    <span className="text-gray-400">{t.open} open</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Leads added this week" sub={`${nf(f.addedThisWeek)} since ${dayLabel(d.week.from)}`} className="lg:col-span-2">
          <Columns data={d.addsByDay.map(x => ({ key: x.day, label: weekdayLabel(x.day), value: x.added, tip: `${nf(x.added)} added · ${dayLabel(x.day)}` }))}
            ariaLabel="Leads added each day this week" tableHead={['Day', 'Leads added']} />
        </Card>
        <Card title="Sources this week" sub="Where this week's leads came from">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-xl bg-green-50 px-3 py-2"><p className="text-xl font-bold text-gray-900">{nf(d.types.WhatsApp)}</p><p className="text-[11px] font-bold text-gray-600">WhatsApp leads</p></div>
            <div className="rounded-xl bg-sky-50 px-3 py-2"><p className="text-xl font-bold text-gray-900">{nf(d.types.Form)}</p><p className="text-[11px] font-bold text-gray-600">Form leads</p></div>
          </div>
          {d.types.none > 0 && <p className="text-[11px] text-gray-400 -mt-1 mb-2">{nf(d.types.none)} without a lead type</p>}
          {d.sources.length === 0 ? <p className="text-sm text-gray-400">No leads added this week.</p> : (
            <ul className="space-y-2">
              {d.sources.map(s => (
                <li key={s.source} className="text-sm">
                  <div className="flex justify-between gap-2"><span className="text-gray-700 truncate">{named(s.source)}</span><b className="text-gray-900 tabular-nums">{nf(s.n)}</b></div>
                  <div className="h-2 rounded-r mt-1" style={{ background: C.track }}>
                    <div className="h-2 rounded-r" style={{ width: `${Math.max(3, (s.n / topSource) * 100)}%`, background: C.leads }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

export default LeadDesk;
